// The routes of CustomerWeb (same paths as the Razor pages, so bookmarks and e-mailed links keep working).
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BffError, ForgotPasswordPage, LoginPage, LogoutPage, SESSION_KEY } from '@qor/web-shared';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CustomerLayout } from './components/CustomerLayout';
import { HomeRedirect, RequireAuth } from './components/RequireAuth';
import { CardAccountPage } from './pages/CardAccount';
import { CardSettingsPage } from './pages/CardSettings';
import { PrivacyPage, SupportPage, TermsPage } from './pages/Legal';
import { OnboardingPage } from './pages/Onboarding';
import { ProfilePage } from './pages/Profile';
import { TransactionsPage } from './pages/Transactions';
import { ZumRailsOnboardingPage } from './pages/ZumRailsOnboarding';
import { landingPath } from './routing';

/** A 401 from the BFF means the session is gone: read it again (RequireAuth then sends the user to sign in). */
export function createQueryClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (err, q) => {
        if (err instanceof BffError && err.status === 401 && q.queryKey !== SESSION_KEY)
          void client.invalidateQueries({ queryKey: SESSION_KEY });
      },
    }),
    defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  });
  return client;
}

export function App({ queryClient }: { queryClient: QueryClient }) {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<LoginPage landing={landingPath} brandLogo showcase="cardholder" />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage showcase="cardholder" />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route element={<CustomerLayout />}>
          {/* reachable without signing in, as in the legacy app */}
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/card" element={<CardAccountPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/card/settings" element={<CardSettingsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/onboarding/mdp" element={<OnboardingPage provider="mdp" />} />
            <Route path="/onboarding/intercash" element={<OnboardingPage provider="intercash" />} />
            <Route path="/onboarding/gsc" element={<OnboardingPage provider="gsc" />} />
            <Route path="/onboarding/central-payments" element={<OnboardingPage provider="opencp" />} />
            <Route path="/onboarding/zumrails" element={<ZumRailsOnboardingPage />} />
          </Route>
        </Route>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}
